import { type ChangeEvent, useState } from "react";
import { filesize } from "filesize";
import type { ProjectFormField } from "../../../api/spark";
import useErrorMessage from "../../../hooks/useErrorMessage";
import TextInput from "../../../design-system/primitives/components/TextInput/TextInput";
import Textarea from "../../../design-system/primitives/components/Textarea/Textarea";
import Tabs from "../../../design-system/primitives/components/Tabs/Tabs";
import Text from "../../../design-system/primitives/components/Text/Text";
import ErrorAlert from "../../../design-system/primitives/components/ErrorAlert/ErrorAlert";
import FileUpload, {
  type FileValue,
  type StoredLink,
  type UploadValue,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB,
} from "./FileUpload";
import styles from "./showroomForm.css";

// ---- Values ----

/** A text field's `string`, or a file field's file / link. */
export type FieldValue = string | UploadValue;

export type Values = Record<string, FieldValue>;

/**
 * What `ShowroomForm` hands every field: the values it owns, the display-ready
 * errors, and the two ways a field changes its value.
 */
export interface ShowroomFormBag {
  /**
   * Editing shows every field on one page, each under its own label;
   * creating shows one field per step, with the field's label as the step's
   * title and its description as the subtitle.
   */
  mode: "create" | "edit";
  values: Values;
  /**
   * An upload that failed, or a rule the value breaks - the latter only once
   * the field has been touched, so a fresh form doesn't open covered in red.
   */
  errors: Record<string, string | undefined>;
  setValue: (key: string, value: FieldValue) => void;
  /** Empties the field without counting as touching it. */
  resetValue: (key: string) => void;
}

export const isEmpty = (value: FieldValue) => value === null || value === "";

const asUpload = (value: FieldValue): UploadValue =>
  typeof value === "string" ? null : value;

const isLink = (value: UploadValue): value is StoredLink =>
  value !== null && !(value instanceof File) && value.type === "link";

// ---- Rules ----

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];
const UPLOAD_TYPES = [
  ...IMAGE_TYPES,
  "audio/mpeg",
  "application/pdf",
  "video/mp4",
];

const IMAGE_ACCEPT = IMAGE_TYPES.join(",");
const UPLOAD_ACCEPT = UPLOAD_TYPES.join(",");

const IMAGE_TYPE_MESSAGE =
  "Ihr könnt nur Bilder im png-, jpeg-, gif-, oder webp-Format hochladen. Wählt eine andere Datei zum Hochladen aus.";
const UPLOAD_TYPE_MESSAGE =
  "Ihr könnt nur Bilder (png, jpeg, gif, webp), Videos (mp4), Audio-Dateien (mp3) oder PDFs hochladen. Wählt eine andere Datei zum Hochladen aus.";

const REQUIRED_MESSAGES: Record<ProjectFormField["type"], string> = {
  singleLine: "Bitte füllt dieses Feld aus.",
  multiLine: "Bitte füllt dieses Feld aus.",
  image: "Bitte wählt ein Bild aus.",
  projectUpload: "Bitte ladet eine Datei hoch oder gebt einen Link an.",
};

const isRequired = (field: ProjectFormField) =>
  Boolean(field.rules?.some((rule) => rule.type === "required"));

const validateFile = (
  file: File,
  allowedTypes: string[],
  typeMessage: string,
) => {
  if (file.size >= MAX_FILE_SIZE) {
    return `Die Datei ${file.name} ist ${filesize(file.size)} groß. Sie darf aber höchstens ${MAX_FILE_SIZE_MB} MB groß sein. Wählt eine kleinere Datei aus.`;
  }
  if (!allowedTypes.some((type) => file.type.startsWith(type))) {
    return typeMessage;
  }
  return null;
};

/**
 * The message for the first rule the value breaks, or `null`. A stored file
 * passed these rules when it was picked, so it always passes.
 */
export const validate = (
  field: ProjectFormField,
  value: FieldValue,
): string | null => {
  if (isEmpty(value)) {
    return isRequired(field) ? REQUIRED_MESSAGES[field.type] : null;
  }
  if (typeof value === "string") {
    for (const rule of field.rules ?? []) {
      if (rule.value === undefined) continue;
      if (rule.type === "minLength" && value.length < rule.value) {
        return `Muss mindestens ${rule.value} Zeichen lang sein`;
      }
      if (rule.type === "maxLength" && value.length > rule.value) {
        return `Darf höchstens ${rule.value} Zeichen lang sein`;
      }
    }
    return null;
  }
  if (value instanceof File) {
    return field.type === "image"
      ? validateFile(value, IMAGE_TYPES, IMAGE_TYPE_MESSAGE)
      : validateFile(value, UPLOAD_TYPES, UPLOAD_TYPE_MESSAGE);
  }
  if (value.type === "link" && !/^https?:\/\//.test(value.data)) {
    return "Link muss mit http:// oder https:// beginnen";
  }
  return null;
};

// ---- Fields ----

interface FieldProps {
  field: ProjectFormField;
  bag: ShowroomFormBag;
  /** The step's placeholder, where the field shows no label of its own. */
  placeholder?: string;
}

/**
 * Whether the field heads itself with its label. In the wizard it doesn't:
 * the step's title is the label, and repeating it above the input says the
 * same thing twice. The label still names the input for screen readers.
 */
const showsLabel = (bag: ShowroomFormBag) => bag.mode === "edit";

/** The field's own label, for the views that show one - see `showsLabel`. */
const FieldLabel = ({ field }: { field: ProjectFormField }) => (
  <Text variant="label" as="p" color="neutral-100">
    {isRequired(field) ? `${field.label} *` : field.label}
  </Text>
);

const TextField = ({ field, bag, placeholder }: FieldProps) => {
  const error = useErrorMessage(bag.errors[field.key]);
  const value = bag.values[field.key];
  const showLabel = showsLabel(bag);
  const inputProps = {
    name: field.key,
    label: showLabel ? field.label : undefined,
    "aria-label": showLabel ? undefined : field.label,
    placeholder: showLabel ? undefined : placeholder,
    required: isRequired(field),
    value: typeof value === "string" ? value : "",
    error: Boolean(error),
    "aria-describedby": error?.id,
  };
  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    bag.setValue(field.key, e.currentTarget.value);

  return (
    <div className={styles.field}>
      {field.type === "multiLine" ? (
        <Textarea {...inputProps} onChange={onChange} rows={6} />
      ) : (
        <TextInput {...inputProps} onChange={onChange} maxLength={120} />
      )}
      {error && <ErrorAlert title={error.message} id={error.id} />}
    </div>
  );
};

const ImageField = ({ field, bag }: FieldProps) => {
  const error = useErrorMessage(bag.errors[field.key]);

  return (
    <div className={styles.field}>
      {showsLabel(bag) && <FieldLabel field={field} />}
      <div className={styles.panel({ error: Boolean(error) })}>
        <FileUpload
          name={field.key}
          label={field.label}
          accept={IMAGE_ACCEPT}
          value={asUpload(bag.values[field.key])}
          onChange={(value) => bag.setValue(field.key, value)}
          error={error}
        />
      </div>
      {error && <ErrorAlert title={error.message} id={error.id} />}
    </div>
  );
};

/**
 * The project itself: a file, or a link to it. Switching tabs drops what the
 * other tab held, so the field never carries both.
 */
const ProjectUploadField = ({ field, bag }: FieldProps) => {
  const value = asUpload(bag.values[field.key]);
  const [tab, setTab] = useState(isLink(value) ? "link" : "file");
  const error = useErrorMessage(bag.errors[field.key]);
  const link = isLink(value) ? value.data : "";

  const setFile = (file: FileValue) => bag.setValue(field.key, file);
  const setLink = (e: ChangeEvent<HTMLInputElement>) =>
    bag.setValue(
      field.key,
      e.currentTarget.value
        ? { type: "link", data: e.currentTarget.value }
        : null,
    );

  return (
    <div className={styles.field}>
      {showsLabel(bag) && <FieldLabel field={field} />}
      <div className={styles.panel({ error: Boolean(error) })}>
        <Tabs
          ariaLabel="Datei oder Link"
          value={tab}
          onValueChange={(nextTab) => {
            setTab(nextTab);
            bag.resetValue(field.key);
          }}
          tabs={[
            {
              value: "file",
              label: "Datei",
              content: (
                <FileUpload
                  name={field.key}
                  label={field.label}
                  accept={UPLOAD_ACCEPT}
                  value={value}
                  onChange={setFile}
                  error={error}
                />
              ),
            },
            {
              value: "link",
              label: "Link",
              content: (
                // The tab it sits in is already called "Link", so the input
                // shows no label of its own and names itself for screen
                // readers the way the file tab's input does: after the field.
                <TextInput
                  name={`${field.key}-link`}
                  aria-label={field.label}
                  placeholder="https://"
                  value={link}
                  onChange={setLink}
                  error={Boolean(error)}
                  aria-describedby={error?.id}
                />
              ),
            },
          ]}
        />
      </div>
      {error && <ErrorAlert title={error.message} id={error.id} />}
    </div>
  );
};

/** One field of the spark's project form, by its type. */
export const FormField = ({ field, bag, placeholder }: FieldProps) => {
  switch (field.type) {
    case "singleLine":
    case "multiLine":
      return <TextField field={field} bag={bag} placeholder={placeholder} />;
    case "image":
      return <ImageField field={field} bag={bag} />;
    case "projectUpload":
      return <ProjectUploadField field={field} bag={bag} />;
  }
};
